import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../api/endpoints';
import { startOnlinePayment } from '../../services/payment';
import PageHeader from '../../components/PageHeader';
import LiveTripMap from '../../components/LiveTripMap';
import {
  Avatar, Badge, Button, Loading, Row,
} from '../../components/Ui';
import { Reveal } from '../../components/Motion';
import {
  connectRealtime, onRealtime, watchTrip, unwatchTrip,
} from '../../services/realtime';
import { useToast } from '../../context/ToastContext';
import { colors } from '../../theme';
import { vehicleLine } from '../../components/VehicleForm';
import { currency, titleCase } from '../../utils/format';

/** What the customer sees at each stage, in their words rather than ours. */
const STAGE = {
  requested: { title: 'Finding you a driver', note: 'Contacting drivers near your pickup point.' },
  accepted: { title: 'Driver on the way', note: 'Share your OTP when the driver arrives.' },
  arrived: { title: 'Your driver is here', note: 'Free waiting time has started — head out now.' },
  ongoing: { title: 'On the way', note: 'Enjoy the trip. Fare is calculated at the end.' },
  completed: { title: 'Trip completed', note: 'Thanks for riding with KAAMWALA.' },
  cancelled: { title: 'Trip cancelled', note: 'This trip was cancelled.' },
  no_drivers: { title: 'No driver available', note: 'Nobody was free nearby. Try again in a moment.' },
};

const RideTrackingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [ride, setRide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.rides.detail(id);
      setRide(res.data);
    } catch (err) {
      toast.error(err.message, 'Could not load trip');
    } finally { setLoading(false); }
  }, [id, toast]);

  useEffect(() => { load(); }, [load]);

  // Status changes on the card; the map subscribes to driver positions itself.
  useEffect(() => {
    connectRealtime().then(() => watchTrip(id));
    const offs = [
      onRealtime('ride:accepted', load),
      onRealtime('ride:arrived', load),
      onRealtime('ride:started', load),
      onRealtime('ride:completed', load),
      onRealtime('ride:no_drivers', load),
      onRealtime('ride:cancelled', () => {
        load();
        toast.error('Your driver cancelled. You can book again.', 'Trip cancelled');
      }),
    ];
    return () => { offs.forEach((fn) => fn()); unwatchTrip(id); };
  }, [id, load, toast]);

  /** A trip held for payment: pay now (drivers are rung once verified) or switch to cash. */
  const payNow = async () => {
    setBusy(true);
    try {
      await startOnlinePayment({ refType: 'ride', refId: id });
      toast.success('Looking for a driver now.', 'Payment received');
    } catch (err) {
      toast.error(err.cancelled ? 'Nothing was charged.' : err.message, err.cancelled ? 'Payment cancelled' : 'Payment not completed');
    } finally { setBusy(false); load(); }
  };
  const payCash = async () => {
    setBusy(true);
    try {
      const res = await api.rides.setPaymentMode(id, { paymentMode: 'cod' });
      toast.success(res.message, 'Paying cash');
    } catch (err) { toast.error(err.message, 'Could not switch'); } finally { setBusy(false); load(); }
  };

  const cancelTrip = async () => {
    const ok = await toast.confirm({
      title: 'Cancel trip',
      message: 'Cancelling after a driver has accepted may attract a small fee.',
      confirmLabel: 'Cancel trip',
      cancelLabel: 'Keep trip',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await api.rides.cancel(id, { reason: 'Cancelled by customer' });
      setRide(res.data.ride);
      if (res.data.cancellationFee) {
        toast.error(`A ${currency(res.data.cancellationFee)} cancellation fee applies.`, 'Trip cancelled');
      }
    } catch (err) {
      toast.error(err.message, 'Could not cancel');
    } finally { setBusy(false); }
  };

  if (loading) return <Loading text="Loading your trip…" />;
  if (!ride) return null;

  const stage = ride.awaitingPayment && ride.status === 'requested'
    ? { title: 'Waiting for payment', note: 'We start looking for a driver as soon as the payment goes through.' }
    : (STAGE[ride.status] || STAGE.requested);
  const isLive = ['requested', 'accepted', 'arrived', 'ongoing'].includes(ride.status);
  const fare = (ride.final && ride.final.total) ? ride.final : ride.estimate;
  const isCourier = ride.serviceType === 'courier';


  /**
   * A stale or unset driver ping (a [0, 0] default, or coordinates saved the
   * other way round) produced readings like "19419 km away". Only trust a fix
   * that is a real point and plausibly the same trip.
   */
  return (
    <div style={{ maxWidth: 900 }}>
      <PageHeader
        title={isCourier ? 'Track delivery' : 'Track your ride'}
        subtitle={ride.rideNo}
        onBack={() => navigate('/activity?tab=rides')}
      />

      {/*
        Uber-style live map: the road the driver is coming by, the car running
        along it, re-routed if they turn off. While the driver is being found
        it previews the trip route. After the trip it just shows the two pins.
      */}
      <LiveTripMap ride={ride} who="customer" height={300} style={{ borderRadius: 18 }} />

      <div className="split" style={{ marginTop: 18 }}>
        <div className="col" style={{ gap: 14 }}>
          <Reveal className="card sh-sm">
            <div className="row-between">
              <h3 className="h3">{stage.title}</h3>
              <Badge value={ride.status} />
            </div>
            <div className="t-small" style={{ marginTop: 3 }}>{stage.note}</div>
          </Reveal>

          {ride.awaitingPayment && ride.status === 'requested' ? (
            <div className="col" style={{ gap: 10 }}>
              <Button title={`Pay ${currency(ride.estimate && ride.estimate.total)} online`} icon="💳" variant="accent" loading={busy} full onClick={payNow} />
              <Button title="Pay cash to driver instead" icon="💵" variant="light" disabled={busy} full onClick={payCash} />
            </div>
          ) : null}

          {/* The OTP the driver will ask for */}
          {['accepted', 'arrived'].includes(ride.status) && ride.otp ? (
            <Reveal
              className="card sh-md"
              style={{ background: colors.primary, border: 0, textAlign: 'center' }}
            >
              <div style={{ color: 'rgba(255,255,255,.8)', fontSize: 10.5, fontWeight: 800, letterSpacing: 1.4 }}>
                SHARE THIS OTP TO START
              </div>
              <div style={{ color: '#fff', fontSize: 40, fontWeight: 900, letterSpacing: 10, marginTop: 6 }}>
                {ride.otp}
              </div>
            </Reveal>
          ) : null}

          {isCourier && ride.deliveryOtp && ride.status === 'ongoing' ? (
            <div className="card sh-md" style={{ background: colors.accent, border: 0, textAlign: 'center' }}>
              <div style={{ color: 'rgba(255,255,255,.85)', fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2 }}>
                DELIVERY OTP — GIVE TO RECEIVER
              </div>
              <div style={{ color: '#fff', fontSize: 40, fontWeight: 900, letterSpacing: 10, marginTop: 6 }}>
                {ride.deliveryOtp}
              </div>
            </div>
          ) : null}

          {ride.driver ? (
            <div className="card sh-sm row" style={{ gap: 12 }}>
              <Avatar name={ride.driver.businessName} size={48} />
              <div className="grow">
                <div className="t-title">{ride.driver.businessName}</div>
                <div className="t-tiny">{vehicleLine(ride.driver.vehicleDetails || {}) || 'Vehicle details on the way'}</div>
                <div className="t-tiny">⭐ {ride.driver.rating || 'New'}</div>
              </div>
              <a
                href={`tel:${ride.driver.phone}`}
                className="press"
                style={{
                  width: 40, height: 40, borderRadius: 12, background: colors.successBg,
                  display: 'grid', placeItems: 'center', fontSize: 16,
                }}
                aria-label="Call driver"
              >
                📞
              </a>
            </div>
          ) : null}

          <div className="card sh-sm">
            <Row label="Pickup" value={(ride.pickup && ride.pickup.address) || '—'} />
            <Row label="Drop" value={(ride.drop && ride.drop.address) || '—'} />
          </div>
        </div>

        <div className="split-side">
          <div className="card sh-sm">
            <Row label={titleCase(ride.vehicleType)} value={`${(fare && fare.distanceKm) || 0} km`} />
            <Row
              label={ride.final && ride.final.total ? 'Fare charged' : 'Estimated fare'}
              value={currency(fare && fare.total)}
              valueStyle={{ fontWeight: 800, fontSize: 16 }}
            />
            {ride.final && ride.final.breakdown ? (
              <>
                <div className="divider" />
                <Row label="Base fare" value={currency(ride.final.breakdown.baseFare)} />
                <Row
                  label={`Distance (${ride.final.breakdown.chargeableKm} km)`}
                  value={currency(ride.final.breakdown.distanceFare)}
                />
                <Row
                  label={`Time (${ride.final.durationMin} min)`}
                  value={currency(ride.final.breakdown.timeFare)}
                />
                {ride.final.breakdown.waitingCharge ? (
                  <Row
                    label={`Waiting (${ride.final.breakdown.billableWaitingMin} min)`}
                    value={currency(ride.final.breakdown.waitingCharge)}
                  />
                ) : null}
                {ride.final.breakdown.nightSurcharge ? (
                  <Row label="Night surcharge" value={currency(ride.final.breakdown.nightSurcharge)} />
                ) : null}
                {ride.final.breakdown.surgeAmount ? (
                  <Row
                    label={`Surge (${ride.final.breakdown.surgeMultiplier}x)`}
                    value={currency(ride.final.breakdown.surgeAmount)}
                  />
                ) : null}
                <Row label="Platform fee" value={currency(ride.final.breakdown.platformFee)} />
              </>
            ) : null}
            <Row label="Payment" value={ride.paymentMode === 'cod' ? 'Cash to driver' : 'Online'} />

            {isLive ? (
              <Button
                title="Cancel trip"
                variant="danger"
                full
                loading={busy}
                style={{ marginTop: 14 }}
                onClick={cancelTrip}
              />
            ) : (
              <Button
                title="Back to home"
                variant="light"
                full
                style={{ marginTop: 14 }}
                onClick={() => navigate('/home')}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RideTrackingPage;
